/**
 * Search folding on the console side (SDD-017 §8.3) — the TypeScript twin of `listquery.Fold`
 * (Go) and `eis_search_fold` (SQL). The mock API compares `=@` and `search` text with it, so a
 * story that types "Mueller" finds Müller the way the service does; `fold.test.ts` holds it to
 * the same `fold` corpus of `fixtures/golden/scenarios.list-search.json` the Go suite reads.
 *
 *  1. lowercase;
 *  2. ß → ss;
 *  3. BOTH forms of the German umlauts: ä→ae ö→oe ü→ue (digraph) and ä→a ö→o ü→u (base);
 *  4. unaccent everything else (é→e, ç→c, ı→i, ø→o);
 *  5. every run of characters that is neither a letter nor a digit becomes one space.
 *
 * Presentation-free and pure: it never reaches the wire. The server folds on its own.
 */

/** The letters unaccent maps that carry no combining mark to strip (Postgres unaccent.rules). */
const SPECIALS: Readonly<Record<string, string>> = {
  ı: 'i',
  ø: 'o',
  đ: 'd',
  ł: 'l',
  ħ: 'h',
  æ: 'ae',
  œ: 'oe',
  ŀ: 'l',
  ŧ: 't',
  ĸ: 'q',
};

function unaccent(s: string): string {
  const stripped = s
    .normalize('NFD')
    .replace(/\p{Mn}/gu, '')
    .normalize('NFC');
  return stripped.replace(/[ıøđłħæœŀŧĸ]/g, (c) => SPECIALS[c] ?? c);
}

function collapse(s: string): string {
  return s.replace(/[^\p{L}\p{N}]+/gu, ' ').trim();
}

const DIGRAPH: Readonly<Record<string, string>> = { ä: 'ae', ö: 'oe', ü: 'ue' };
const BASE: Readonly<Record<string, string>> = { ä: 'a', ö: 'o', ü: 'u' };

/**
 * The folded forms of `s`: one form, or two (digraph first, then base) when `s` holds an umlaut.
 * A blank `s` folds to nothing.
 */
export function foldSearch(s: string): string[] {
  const lowered = s.toLowerCase().replace(/ß/g, 'ss');
  const digraph = collapse(unaccent(lowered.replace(/[äöü]/g, (c) => DIGRAPH[c] ?? c)));
  const base = collapse(unaccent(lowered.replace(/[äöü]/g, (c) => BASE[c] ?? c)));
  if (digraph === '' && base === '') return [];
  if (digraph === base) return [digraph];
  return [digraph, base];
}

/** The indexed spelling of `s`: its forms joined by one space. */
export function foldSearchText(s: string): string {
  return foldSearch(s).join(' ');
}

/**
 * An identifier's folded form (`_ids`): the base form, letters and digits only, upper case —
 * `S-AB 123` → `SAB123`, so spaces and hyphens never stop an identifier match.
 */
export function foldIdentifier(s: string): string {
  const base = unaccent(
    s
      .toLowerCase()
      .replace(/ß/g, 'ss')
      .replace(/[äöü]/g, (c) => BASE[c] ?? c),
  );
  return base.replace(/[^\p{L}\p{N}]/gu, '').toUpperCase();
}
